import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBar, BottomNav } from "../components/Shell";
import Ico from "../components/Ico";
import { useAppStore } from "../components/AppStore";
import { chores, categories } from "../mock";
import { jobStatusMeta } from "./NeighborHome";
import { Search, SlidersHorizontal, Star, Clock, MapPin, ShieldCheck, CheckCircle2, X, Store } from "lucide-react";

export default function Explore() {
  const navigate = useNavigate();
  const { statusOf, postedJobs, kidAcceptJob } = useAppStore();
  const [cat, setCat] = useState("All");
  const [tab, setTab] = useState("Discover");
  const [q, setQ] = useState("");
  const [accepting, setAccepting] = useState(null);
  const [sent, setSent] = useState(null);

  const openPosted = postedJobs.filter((j) => j.status === "open" && (cat === "All" || j.category === cat) && (!q || j.title.toLowerCase().includes(q.toLowerCase())));
  const myPosted = postedJobs.filter((j) => j.status === "kid_accepted" || j.status === "parent_approved");

  let list = chores;
  if (cat !== "All") list = list.filter((c) => c.category === cat);
  if (q) list = list.filter((c) => c.title.toLowerCase().includes(q.toLowerCase()));
  if (tab === "Accepted") list = chores.filter((c) => statusOf(c.id));
  if (tab === "Completed") list = [];

  const accept = () => {
    if (!accepting) return;
    kidAcceptJob(accepting.id);
    setSent(accepting);
    setAccepting(null);
  };

  return (
    <div className="gd-screen bg-[#eef1fb]">
      <div className="gd-header-grad flex-shrink-0 px-5 pb-5" style={{ borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
        <StatusBar dark={false} />
        <div className="mt-1 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-white">Explore Chores</h1>
            <p className="mt-0.5 text-sm text-white/70">{chores.length} opportunities near you</p>
          </div>
          <button className="gd-tap grid h-10 w-10 place-items-center rounded-full bg-white/15">
            <SlidersHorizontal size={18} color="#fff" />
          </button>
        </div>
        <div className="mt-4 flex items-center gap-2.5 rounded-2xl bg-white px-4 py-3">
          <Search size={18} className="text-[#a3abc9]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chores..." className="w-full bg-transparent text-sm font-medium text-[#1f2a52] outline-none placeholder:text-[#b6bdd6]" />
        </div>
      </div>

      <div className="gd-scroll px-4 pb-6">
        {/* categories */}
        <div className="flex gap-2 overflow-x-auto py-4" style={{ scrollbarWidth: "none" }}>
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} className="gd-tap flex-shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-all"
              style={{ background: cat === c ? "#3b57c9" : "#fff", color: cat === c ? "#fff" : "#5b648a", boxShadow: "0 4px 12px rgba(31,42,82,.05)" }}>
              {c}
            </button>
          ))}
        </div>

        {/* tabs */}
        <div className="flex gap-5 border-b border-[#e6e9f5] pb-0">
          {["Discover", "Accepted", "Completed"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className="relative pb-2.5 text-sm font-bold transition-colors"
              style={{ color: tab === t ? "#2b3f96" : "#a3abc9" }}>
              {t}
              {tab === t && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#f5911e]" />}
            </button>
          ))}
        </div>

        {/* posted by neighbors (Discover) */}
        {tab === "Discover" && openPosted.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2">
              <Store size={16} className="text-[#f5911e]" />
              <h3 className="text-sm font-extrabold text-[#1f2a52]">New from Neighbors</h3>
              <span className="rounded-full bg-[#fdece0] px-2 py-0.5 text-[9px] font-bold text-[#e97f0c]">Verified posters</span>
            </div>
            <div className="space-y-2.5">
              {openPosted.map((j) => (
                <div key={j.id} className="flex items-center gap-3 rounded-[20px] bg-white p-3 shadow-[0_8px_22px_rgba(31,42,82,0.06)]">
                  <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-2xl" style={{ background: `${j.tint}18` }}><Ico name={j.icon} size={22} color={j.tint} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-extrabold text-[#1f2a52]">{j.title}</p>
                    <p className="truncate text-[10px] font-semibold text-[#9aa3c0]">{j.schedule}</p>
                    <p className="mt-0.5 text-sm font-black text-[#f5911e]">${j.pay} <span className="text-[9px] font-bold text-[#8b5cf6]">+{j.xp} XP</span></p>
                  </div>
                  <button onClick={() => setAccepting(j)} className="gd-blue-btn flex-shrink-0 rounded-full px-4 py-2 text-xs font-extrabold">Accept</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* my accepted neighbor jobs (Accepted) */}
        {tab === "Accepted" && myPosted.length > 0 && (
          <div className="mt-4 space-y-2.5">
            {myPosted.map((j) => {
              const m = jobStatusMeta[j.status];
              return (
                <div key={j.id} className="flex items-center gap-3 rounded-[20px] bg-white p-3 shadow-[0_8px_22px_rgba(31,42,82,0.06)]">
                  <div className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-2xl" style={{ background: `${j.tint}18` }}><Ico name={j.icon} size={20} color={j.tint} /></div>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-extrabold text-[#1f2a52]">{j.title}</p><p className="text-[10px] font-semibold text-[#9aa3c0]">{j.schedule}</p></div>
                  <span className="flex-shrink-0 rounded-full px-2 py-1 text-[9px] font-bold" style={{ background: m.bg, color: m.color }}>{j.status === "parent_approved" ? "Approved" : "Awaiting parent"}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* grid */}
        {list.length === 0 && !(tab === "Accepted" && myPosted.length > 0) ? (
          <div className="mt-16 flex flex-col items-center text-center text-[#9aa3c0]">
            <ShieldCheck size={40} className="mb-3 text-[#c7cde6]" />
            <p className="text-sm font-semibold">{tab === "Completed" ? "No completed chores yet" : "Nothing here yet"}</p>
            <p className="mt-1 text-xs">Accept a chore to see it here.</p>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-3 gd-stagger">
            {list.map((c) => (
              <button key={c.id} onClick={() => navigate(`/task/${c.id}`)} className="gd-tap flex flex-col rounded-[20px] bg-white p-3 text-left shadow-[0_8px_22px_rgba(31,42,82,0.06)]">
                <div className="flex items-start justify-between">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl" style={{ background: `${c.tint}18` }}>
                    <Ico name={c.icon} size={22} color={c.tint} />
                  </div>
                  {c.urgent && <span className="rounded-full bg-[#fdece0] px-1.5 py-0.5 text-[8px] font-extrabold text-[#e97f0c]">URGENT</span>}
                </div>
                <p className="mt-2 text-sm font-extrabold leading-tight text-[#1f2a52]">{c.title}</p>
                <div className="mt-1 flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={10} fill={i < Math.round(c.rating) ? "#f5b301" : "none"} color="#f5b301" />)}
                  <span className="ml-1 text-[9px] font-semibold text-[#9aa3c0]">({c.reviews})</span>
                </div>
                <p className="mt-1.5 text-xl font-black text-[#f5911e]">${c.pay}</p>
                <div className="mt-1 flex items-center gap-2 text-[9px] font-semibold text-[#9aa3c0]">
                  <span className="flex items-center gap-0.5"><Clock size={9} /> {c.duration}</span>
                  <span className="flex items-center gap-0.5"><MapPin size={9} /> {c.distance}</span>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-[#f0f2fa] pt-2">
                  <span className="text-[9px] font-bold text-[#8b5cf6]">+{c.xp} XP</span>
                  {statusOf(c.id) && <span className="rounded-full bg-[#eaf6ea] px-1.5 py-0.5 text-[8px] font-bold text-[#3d8f34]">{statusOf(c.id) === "approved" ? "Approved" : "Pending"}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <BottomNav />

      {/* accept confirm */}
      {accepting && (
        <div className="absolute inset-0 z-50 flex items-end bg-black/40" onClick={() => setAccepting(null)}>
          <div className="gd-enter w-full rounded-t-[28px] bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
            <div className="flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center rounded-2xl" style={{ background: `${accepting.tint}18` }}><Ico name={accepting.icon} size={26} color={accepting.tint} /></div>
              <div><h3 className="text-lg font-black text-[#1f2a52]">{accepting.title}</h3><p className="text-xs font-semibold text-[#9aa3c0]">{accepting.schedule} · ${accepting.pay}</p></div>
            </div>
            <p className="mt-3 rounded-2xl bg-[#eef2ff] p-3 text-[12px] leading-relaxed text-[#5b648a]">
              Accepting sends this job to your parent for approval. You can start once they review the pay & schedule and accept liability.
            </p>
            <button onClick={accept} className="gd-blue-btn mt-4 w-full rounded-2xl py-4 font-extrabold">Accept & Send to Parent</button>
            <button onClick={() => setAccepting(null)} className="mt-2 w-full rounded-2xl py-3 font-bold text-[#5b648a]">Cancel</button>
          </div>
        </div>
      )}

      {/* sent confirmation */}
      {sent && (
        <div className="absolute inset-0 z-50 flex items-end bg-black/40">
          <div className="gd-enter relative w-full rounded-t-[28px] bg-white p-6 pb-8 text-center">
            <button onClick={() => setSent(null)} className="absolute right-5 top-5 text-[#a3abc9]"><X size={20} /></button>
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#eef2ff]"><CheckCircle2 size={34} className="text-[#3b57c9]" /></div>
            <h3 className="mt-3 text-xl font-black text-[#1f2a52]">Sent to Your Parent</h3>
            <p className="mt-1 text-sm text-[#5b648a]"><b>{sent.title}</b> is now waiting for a parent to approve. Switch to the Parent view to approve it.</p>
            <button onClick={() => setSent(null)} className="gd-blue-btn mt-5 w-full rounded-2xl py-3.5 font-extrabold">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}
